import React, { useState, useEffect } from 'react';
import './UserAccessModal.css';
import { supabase } from '../../lib/supabase';
import { logUserActivity } from '../../lib/activityLogger';
import { UserIdGenerator } from '../../lib/userIdGenerator';

const UserAccessModal = ({ isOpen, onClose, schoolId, schoolData, onUserAdded }) => {
    const [users, setUsers] = useState([]);
    const [existingTeachers, setExistingTeachers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addMode, setAddMode] = useState('existing'); // 'existing' or 'new'
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    const [selectedTeacherId, setSelectedTeacherId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [grantSuperAdmin, setGrantSuperAdmin] = useState(false);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'teacher',
        password: ''
    });

    const roleOptions = [
        { value: 'teacher', label: 'Teacher' },
        { value: 'staff', label: 'Staff' },
        { value: 'coordinator', label: 'Coordinator' },
        { value: 'admin', label: 'Admin' }
    ];

    // Generate random 6-digit password
    const generateRandomPassword = () => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    };

    // Fetch users with dashboard access
    const fetchUsers = async () => {
        if (!schoolId) return;

        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('users')
                .select('*')
                .eq('school_id', schoolId)
                .eq('allow_dashboard_access', true)
                .order('created_at', { ascending: false });

            if (error) {showNotification('error', 'Failed to load users');
            } else {
                setUsers(data || []);
            }
        } catch (err) {showNotification('error', 'Failed to load users');
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch existing teachers without dashboard access
    const fetchExistingTeachers = async () => {
        if (!schoolId) return;

        try {
            const { data, error } = await supabase
                .from('users')
                .select('*')
                .eq('school_id', schoolId)
                .eq('role', 'teacher')
                .eq('allow_dashboard_access', false)
                .eq('is_active', true)
                .order('first_name', { ascending: true });

            if (error) {} else {
                setExistingTeachers(data || []);
            }
        } catch (err) {
            }
    };

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            fetchExistingTeachers();
            setFormData(prev => ({ ...prev, password: generateRandomPassword() }));
        }
    }, [isOpen, schoolId]);

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            role: 'teacher',
            password: generateRandomPassword()
        });
        setSelectedTeacherId('');
        setSearchQuery('');
        setAddMode('existing');
        setGrantSuperAdmin(false);
    };

    // Grant access to existing teacher
    const handleGrantAccess = async () => {
        if (!selectedTeacherId) {
            showNotification('error', 'Please select a teacher');
            return;
        }

        setIsSubmitting(true);
        try {
            const selectedTeacher = existingTeachers.find(t => t.id === selectedTeacherId);

            const { error } = await supabase
                .from('users')
                .update({
                    allow_dashboard_access: true,
                    is_super_admin: grantSuperAdmin
                })
                .eq('id', selectedTeacherId);

            if (error) {showNotification('error', `Failed to grant access: ${error.message}`);
            } else {
                const userName = `${selectedTeacher?.first_name} ${selectedTeacher?.last_name}`;
                await logUserActivity.updated(`Granted dashboard access: ${userName}`, schoolId);

                showNotification('success', `Dashboard access granted to "${userName}"`);
                resetForm();
                setShowAddForm(false);
                fetchUsers();
                fetchExistingTeachers();
                onUserAdded && onUserAdded();
            }
        } catch (err) {showNotification('error', 'Failed to grant access');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Add new user with dashboard access
    const handleSubmitNewUser = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const schoolCode = schoolData?.school_id;
            if (!schoolCode) {
                showNotification('error', 'School code not found');
                setIsSubmitting(false);
                return;
            }

            const userId = await UserIdGenerator.generateUserId(schoolCode);
            if (!userId) {
                showNotification('error', 'Failed to generate user ID');
                setIsSubmitting(false);
                return;
            }

            const { error } = await supabase
                .from('users')
                .insert([{
                    user_id: userId,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    school_id: schoolId,
                    email: formData.email || null,
                    phone: formData.phone || null,
                    role: formData.role,
                    password: formData.password,
                    allow_dashboard_access: true,
                    is_super_admin: grantSuperAdmin,
                    is_active: true,
                    created_at: new Date().toISOString()
                }]);

            if (error) {showNotification('error', `Failed to add user: ${error.message}`);
            } else {
                const userName = `${formData.first_name} ${formData.last_name}`;
                await logUserActivity.created(userName, 'Dashboard Access Users', schoolId);

                showNotification('success', `User "${userName}" added with ID: ${userId}`);
                resetForm();
                setShowAddForm(false);
                fetchUsers();
                onUserAdded && onUserAdded();
            }
        } catch (err) {showNotification('error', 'Failed to add user');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;

        try {
            const { error } = await supabase
                .from('users')
                .update({ allow_dashboard_access: false })
                .eq('id', userToDelete.id);

            if (error) {showNotification('error', 'Failed to remove dashboard access');
            } else {
                const userName = `${userToDelete.first_name} ${userToDelete.last_name}`;
                await logUserActivity.updated(`Removed dashboard access: ${userName}`, schoolId);

                showNotification('success', `Dashboard access removed for "${userName}"`);
                fetchUsers();
                fetchExistingTeachers();
                onUserAdded && onUserAdded();
            }
        } catch (err) {showNotification('error', 'Failed to remove dashboard access');
        } finally {
            setShowDeleteConfirm(false);
            setUserToDelete(null);
        }
    };

    // Toggle super admin status for a user
    const toggleSuperAdmin = async (user) => {
        // Optimistic update - update local state immediately
        const newStatus = !user.is_super_admin;
        setUsers(prevUsers =>
            prevUsers.map(u =>
                u.id === user.id ? { ...u, is_super_admin: newStatus } : u
            )
        );

        try {
            const { error } = await supabase
                .from('users')
                .update({ is_super_admin: newStatus })
                .eq('id', user.id);

            if (error) {
                // Revert on error
                setUsers(prevUsers =>
                    prevUsers.map(u =>
                        u.id === user.id ? { ...u, is_super_admin: !newStatus } : u
                    )
                );showNotification('error', 'Failed to update admin status');
            } else {
                const userName = `${user.first_name} ${user.last_name}`;
                await logUserActivity.updated(
                    `${newStatus ? 'Granted' : 'Removed'} super admin: ${userName}`,
                    schoolId
                );
                showNotification('success',
                    `${userName} is ${newStatus ? 'now a super admin' : 'no longer a super admin'}`
                );
            }
        } catch (err) {
            // Revert on error
            setUsers(prevUsers =>
                prevUsers.map(u =>
                    u.id === user.id ? { ...u, is_super_admin: !newStatus } : u
                )
            );showNotification('error', 'Failed to update admin status');
        }
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setUserToDelete(null);
    };

    // Filter existing teachers by search query
    const filteredTeachers = existingTeachers.filter(teacher => {
        const fullName = `${teacher.first_name} ${teacher.last_name}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase());
    });

    if (!isOpen) return null;

    return (
        <div className="user-access-modal-overlay" onClick={onClose}>
            <div className="user-access-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="user-access-modal-header">
                    <div className="user-access-modal-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <h3>Manage Dashboard Access</h3>
                    <button className="user-access-modal-close" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                {/* Notification */}
                {notification.show && (
                    <div className={`user-access-notification ${notification.type}`}>
                        {notification.message}
                    </div>
                )}

                {/* Body */}
                <div className="user-access-modal-body">
                    {!showAddForm ? (
                        <>
                            {/* Add User Button */}
                            <button
                                className="user-access-add-btn"
                                onClick={() => setShowAddForm(true)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Grant Dashboard Access
                            </button>

                            {/* Users List */}
                            <div className="user-access-list">
                                <h4>Users with Dashboard Access ({users.length})</h4>

                                {isLoading ? (
                                    <div className="user-access-loading">
                                        <div className="user-access-spinner"></div>
                                        Loading users...
                                    </div>
                                ) : users.length === 0 ? (
                                    <div className="user-access-empty">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <p>No users with dashboard access yet</p>
                                        <p className="user-access-empty-hint">Click "Grant Dashboard Access" to get started</p>
                                    </div>
                                ) : (
                                    <div className="user-access-table-container">
                                        <table className="user-access-table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>User ID</th>
                                                    <th>Role</th>
                                                    <th>Password</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map((user) => (
                                                    <tr key={user.id}>
                                                        <td>
                                                            <div className="user-access-name">
                                                                <span className="user-access-avatar">
                                                                    {(user.first_name?.[0] || '') + (user.last_name?.[0] || '')}
                                                                </span>
                                                                {user.first_name} {user.last_name}
                                                            </div>
                                                        </td>
                                                        <td><code>{user.user_id}</code></td>
                                                        <td><span className="user-access-role-badge">{user.role}</span></td>
                                                        <td><code>{user.password}</code></td>
                                                        <td>
                                                            <div className="user-access-actions">
                                                                <label className="user-access-admin-toggle-wrapper" title="Toggle Super Admin">
                                                                    <span className="user-access-admin-label">
                                                                        {user.is_super_admin ? 'Admin' : 'User'}
                                                                    </span>
                                                                    <div className="user-access-admin-toggle">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={user.is_super_admin || false}
                                                                            onChange={() => toggleSuperAdmin(user)}
                                                                        />
                                                                        <span className="user-access-admin-slider"></span>
                                                                    </div>
                                                                </label>
                                                                <button
                                                                    className="user-access-delete-btn"
                                                                    onClick={() => handleDeleteClick(user)}
                                                                    title="Remove Dashboard Access"
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        /* Add/Grant Access Form */
                        <div className="user-access-form">
                            <button
                                type="button"
                                className="user-access-back-btn"
                                onClick={() => { setShowAddForm(false); resetForm(); }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Back to List
                            </button>

                            {/* Mode Toggle */}
                            <div className="user-access-mode-toggle">
                                <button
                                    type="button"
                                    className={`user-access-mode-btn ${addMode === 'existing' ? 'active' : ''}`}
                                    onClick={() => setAddMode('existing')}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M20 8L20 14M17 11H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Existing Teacher
                                </button>
                                <button
                                    type="button"
                                    className={`user-access-mode-btn ${addMode === 'new' ? 'active' : ''}`}
                                    onClick={() => setAddMode('new')}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M20 8V14M17 11H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    New User
                                </button>
                            </div>

                            {addMode === 'existing' ? (
                                /* Grant access to existing teacher */
                                <div className="user-access-existing-section">
                                    <h4>Grant Access to Existing Teacher</h4>
                                    <p className="user-access-section-hint">
                                        Select a teacher from your school to grant them dashboard access
                                    </p>

                                    {/* Search Input */}
                                    <div className="user-access-search">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder="Search teachers by name..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>

                                    {/* Teacher List */}
                                    <div className="user-access-teacher-list">
                                        {filteredTeachers.length === 0 ? (
                                            <div className="user-access-no-teachers">
                                                {existingTeachers.length === 0
                                                    ? 'No teachers available to grant access'
                                                    : 'No teachers match your search'}
                                            </div>
                                        ) : (
                                            filteredTeachers.map((teacher) => (
                                                <label
                                                    key={teacher.id}
                                                    className={`user-access-teacher-item ${selectedTeacherId === teacher.id ? 'selected' : ''}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="selectedTeacher"
                                                        value={teacher.id}
                                                        checked={selectedTeacherId === teacher.id}
                                                        onChange={(e) => setSelectedTeacherId(e.target.value)}
                                                    />
                                                    <span className="user-access-teacher-avatar">
                                                        {(teacher.first_name?.[0] || '') + (teacher.last_name?.[0] || '')}
                                                    </span>
                                                    <div className="user-access-teacher-info">
                                                        <span className="user-access-teacher-name">
                                                            {teacher.first_name} {teacher.last_name}
                                                        </span>
                                                        <span className="user-access-teacher-id">
                                                            ID: {teacher.user_id} • Password: {teacher.password}
                                                        </span>
                                                    </div>
                                                </label>
                                            ))
                                        )}
                                    </div>

                                    {/* Super Admin Toggle */}
                                    <div className="user-access-super-admin-option">
                                        <label className="user-access-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={grantSuperAdmin}
                                                onChange={(e) => setGrantSuperAdmin(e.target.checked)}
                                            />
                                            <span className="user-access-checkbox-text">
                                                <strong>Grant Super Admin Access</strong>
                                                <small>User can manage dashboard access for others</small>
                                            </span>
                                        </label>
                                    </div>

                                    <div className="user-access-form-actions">
                                        <button
                                            type="button"
                                            className="user-access-cancel-btn"
                                            onClick={() => { setShowAddForm(false); resetForm(); }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="user-access-submit-btn"
                                            onClick={handleGrantAccess}
                                            disabled={isSubmitting || !selectedTeacherId}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="user-access-spinner"></span>
                                                    Granting...
                                                </>
                                            ) : (
                                                'Grant Access'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* Add new user form */
                                <form onSubmit={handleSubmitNewUser}>
                                    <h4>Add New User with Dashboard Access</h4>

                                    <div className="user-access-form-row">
                                        <div className="user-access-form-group">
                                            <label htmlFor="first_name">First Name *</label>
                                            <input
                                                type="text"
                                                id="first_name"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Enter first name"
                                            />
                                        </div>
                                        <div className="user-access-form-group">
                                            <label htmlFor="last_name">Last Name *</label>
                                            <input
                                                type="text"
                                                id="last_name"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Enter last name"
                                            />
                                        </div>
                                    </div>

                                    <div className="user-access-form-row">
                                        <div className="user-access-form-group">
                                            <label htmlFor="email">Email</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Enter email (optional)"
                                            />
                                        </div>
                                        <div className="user-access-form-group">
                                            <label htmlFor="phone">Phone</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder="Enter phone (optional)"
                                            />
                                        </div>
                                    </div>

                                    <div className="user-access-form-row">
                                        <div className="user-access-form-group">
                                            <label htmlFor="role">Role *</label>
                                            <select
                                                id="role"
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                {roleOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="user-access-form-group">
                                            <label htmlFor="password">Password *</label>
                                            <div className="user-access-password-input">
                                                <input
                                                    type="text"
                                                    id="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="6-digit password"
                                                />
                                                <button
                                                    type="button"
                                                    className="user-access-regenerate-btn"
                                                    onClick={() => setFormData(prev => ({ ...prev, password: generateRandomPassword() }))}
                                                    title="Generate new password"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M1 4V10H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M23 20V14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M20.49 9C19.9828 7.56678 19.1209 6.28438 17.9845 5.27542C16.8482 4.26646 15.4745 3.56449 13.9917 3.2349C12.5089 2.90532 10.9652 2.95903 9.50481 3.39133C8.04437 3.82363 6.71475 4.62135 5.64 5.71L1 10M23 14L18.36 18.29C17.2853 19.3787 15.9556 20.1764 14.4952 20.6087C13.0348 21.041 11.4911 21.0947 10.0083 20.7651C8.52547 20.4355 7.1518 19.7335 6.01547 18.7246C4.87913 17.7156 4.01717 16.4332 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Super Admin Toggle */}
                                    <div className="user-access-super-admin-option">
                                        <label className="user-access-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={grantSuperAdmin}
                                                onChange={(e) => setGrantSuperAdmin(e.target.checked)}
                                            />
                                            <span className="user-access-checkbox-text">
                                                <strong>Grant Super Admin Access</strong>
                                                <small>User can manage dashboard access for others</small>
                                            </span>
                                        </label>
                                    </div>

                                    <div className="user-access-form-actions">
                                        <button
                                            type="button"
                                            className="user-access-cancel-btn"
                                            onClick={() => { setShowAddForm(false); resetForm(); }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="user-access-submit-btn"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="user-access-spinner"></span>
                                                    Adding...
                                                </>
                                            ) : (
                                                'Add User'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="user-access-confirm-overlay">
                        <div className="user-access-confirm-modal">
                            <h4>Remove Dashboard Access?</h4>
                            <p>
                                Are you sure you want to remove dashboard access for{' '}
                                <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong>?
                            </p>
                            <p className="user-access-confirm-hint">
                                The user will no longer be able to login to the school dashboard.
                            </p>
                            <div className="user-access-confirm-actions">
                                <button className="user-access-cancel-btn" onClick={cancelDelete}>
                                    Cancel
                                </button>
                                <button className="user-access-confirm-delete-btn" onClick={confirmDelete}>
                                    Remove Access
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserAccessModal;
